import { useState } from 'react';
import { Link } from 'react-router-dom';
import jsPDF from 'jspdf';
import { Alert, Button, PageHeader, Spinner, tokens } from '@aigovops/design-system';
import { useRequiredProfile } from '../lib/auth';
import { useOptionalWorkspace } from '../lib/workspace';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import {
  exportAssessmentJson,
  exportAssessmentXlsx,
  getProgress,
  getReadinessHistory,
  listRegister,
  type ReadinessHistoryPoint,
} from '../lib/api';
import type { RiskItem } from '@aigovops/core';
import { BurnDownChart } from '../components/dashboard/BurnDownChart';
import { fmtDate } from '../lib/format';

export function Reports() {
  const profileId = useRequiredProfile();
  const ws = useOptionalWorkspace();
  // V98-3-1: was `./register/citations`, which resolved under /deliverables (a dead link).
  const citationsHref = ws ? `/w/${ws.workspace.slug}/app/register/citations` : '/register';
  const progress = useWorkspaceQuery(
    (slug) => queryKeys.progress(slug, profileId),
    () => getProgress(profileId),
  );
  // PR-Cluster-5-a: snapshot history consumer for the workspace-scoped
  // /readiness/:profileId/history endpoint (backend shipped in
  // PR-Cluster-1-b as commit 86ef5c9). Longer window than Dashboard
  // — Deliverables is for the reviewer who wants the full picture, not
  // scan-mode. Skipped when no workspace slug (legacy sessions).
  const history = useWorkspaceQuery(
    (slug) => queryKeys.readinessHistory(slug, profileId),
    (slug) => getReadinessHistory(slug, profileId, '26w'),
  );
  const [busy, setBusy] = useState<null | 'json' | 'xlsx' | 'pdf'>(null);
  const [error, setError] = useState<string | null>(null);

  function download(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function downloadJson() {
    setBusy('json');
    setError(null);
    try {
      const data = await exportAssessmentJson(profileId);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      download(blob, `compliance-${profileId}-${today()}.json`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'JSON export failed');
    } finally {
      setBusy(null);
    }
  }

  async function downloadXlsx() {
    setBusy('xlsx');
    setError(null);
    try {
      const blob = await exportAssessmentXlsx(profileId);
      download(blob, `compliance-${profileId}-${today()}.xlsx`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'XLSX export failed');
    } finally {
      setBusy(null);
    }
  }

  async function downloadPdf() {
    setBusy('pdf');
    setError(null);
    try {
      // Fetch export + full register in parallel. The register drives the
      // citation-chain appendix (§7.5) — brand-guide Pillar 2, punch P10.
      const [data, register] = await Promise.all([
        exportAssessmentJson(profileId),
        listRegister({ limit: 500 }),
      ]);
      const doc = new jsPDF({ unit: 'pt', format: 'letter' });
      renderPdf(doc, data, register.data);
      doc.save(`compliance-${profileId}-${today()}.pdf`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'PDF export failed');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-6)' }}>
      <PageHeader
        title="Deliverables"
        subtitle="Board-ready summaries + machine-readable payloads for GRC integration."
      />

      {error ? <Alert variant="error">{error}</Alert> : null}

      <section
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          background: 'var(--surface)',
          padding: 'var(--space-4)',
          fontFamily: 'var(--font-ui)',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)', marginBottom: 'var(--space-2)' }}>
          Downloads
        </h2>
        <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)', marginBottom: 'var(--space-3)' }}>
          Exports reflect your current readiness state. Review your Dashboard score before sharing with stakeholders.
        </div>
        <div
          style={{
            display: 'grid',
            gap: 'var(--space-3)',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          }}
        >
          <ExportCard
            title="PDF"
            description="Board-ready readiness summary with gacpro wordmark, sourced scores, and top roadmap. Print via Cmd+P for browser-native output using the full design-system stylesheet."
            action="Download PDF"
            onClick={downloadPdf}
            busy={busy === 'pdf'}
          />
          <ExportCard
            title="XLSX"
            description="Full register + roadmap + outcomes as sheets. Import into any GRC tool."
            action="Download XLSX"
            onClick={downloadXlsx}
            busy={busy === 'xlsx'}
          />
          <ExportCard
            title="JSON"
            description="Machine-readable payload for API integrations and archival."
            action="Download JSON"
            onClick={downloadJson}
            busy={busy === 'json'}
          />
        </div>
        <p
          style={{
            marginTop: 'var(--space-3)',
            fontSize: 'var(--font-size-3xs)',
            color: 'var(--text-muted)',
          }}
        >
          Board exports include the full citation chain as an appendix.{' '}
          <Link to={citationsHref} style={{ color: 'var(--text-muted)' }}>
            Browse the citation chain →
          </Link>
        </p>
      </section>

      <section
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          background: 'var(--surface)',
          padding: 'var(--space-4)',
          fontFamily: 'var(--font-ui)',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)', marginBottom: 'var(--space-3)' }}>
          Progress by severity
        </h2>
        {progress.loading ? (
          <Spinner label="Loading progress" />
        ) : progress.error ? (
          <Alert variant="error">{progress.error}</Alert>
        ) : progress.data ? (
          <BurnDownChart progress={progress.data} />
        ) : null}
      </section>

      {/* PR-Cluster-5-a (docs/ux-design-plan.md §10): snapshot history
          table. Deliverables is the reviewer surface, not scan-mode — a
          table with both as-shipped + restated scores per snapshot
          is more useful here than a sparkline, because the reviewer
          wants the numbers and the version-shift audit trail, not
          the visual shape. Self-hides when no workspace slug or
          history has fewer than 2 points (a single point isn't a
          history worth tabulating). */}
      <SnapshotHistorySection
        loading={history.loading}
        error={history.error}
        points={history.data?.points ?? null}
        activeVersion={history.data?.methodology.active_version ?? null}
      />
    </div>
  );
}

// ---- Snapshot history table -----------------------------------------

function SnapshotHistorySection({
  loading,
  error,
  points,
  activeVersion,
}: {
  loading: boolean;
  error: string | null;
  points: ReadinessHistoryPoint[] | null;
  activeVersion: string | null;
}) {
  if (loading) {
    return (
      <section
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          background: 'var(--surface)',
          padding: 'var(--space-4)',
        }}
      >
        <Spinner label="Loading snapshot history" />
      </section>
    );
  }
  if (error) return <Alert variant="error">{error}</Alert>;
  if (!points || points.length < 2 || activeVersion == null) return null;
  // Reverse so most-recent-first — reviewer flow reads newest → oldest.
  const rows = [...points].reverse();
  return (
    <section
      aria-labelledby="snapshot-history-heading"
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        padding: 'var(--space-4)',
        fontFamily: 'var(--font-ui)',
      }}
    >
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 'var(--space-3)',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h2
            id="snapshot-history-heading"
            style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)' }}
          >
            Snapshot history
          </h2>
          <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)' }}>
            Last 26 weeks. Restated column recomputes each historical point under
            methodology v{activeVersion}.
          </p>
        </div>
      </header>
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 'var(--font-size-2xs)',
            color: 'var(--text)',
          }}
        >
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)' }}>
              <th style={{ padding: '8px 12px 8px 0', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Captured</th>
              <th style={{ padding: '8px 12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>As shipped</th>
              <th style={{ padding: '8px 12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Methodology</th>
              <th style={{ padding: '8px 12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>Restated</th>
              <th style={{ padding: '8px 12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>Δ</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const restated = r.restated.score;
              const delta = restated != null ? restated - r.as_shipped.score : null;
              return (
                <tr key={r.snapshot_id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px 12px 8px 0', fontFamily: 'var(--font-mono)' }}>
                    <time dateTime={r.taken_at}>{fmtDate(r.taken_at)}</time>
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                    {r.as_shipped.score}
                  </td>
                  <td style={{ padding: '8px 12px', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
                    {r.as_shipped.methodology_version ? (
                      <span
                        title={
                          r.as_shipped.methodology_version === activeVersion
                            ? 'Same as current methodology.'
                            : 'This snapshot was captured under an older methodology; restated column reflects the current one.'
                        }
                      >
                        v{r.as_shipped.methodology_version}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-dim)' }}>—</span>
                    )}
                  </td>
                  <td
                    style={{
                      padding: '8px 12px',
                      textAlign: 'right',
                      fontFamily: 'var(--font-mono)',
                      fontVariantNumeric: 'tabular-nums',
                      color: restated == null ? 'var(--text-dim)' : 'var(--text)',
                    }}
                  >
                    {restated ?? '—'}
                  </td>
                  <td
                    style={{
                      padding: '8px 12px',
                      textAlign: 'right',
                      fontFamily: 'var(--font-mono)',
                      fontVariantNumeric: 'tabular-nums',
                      color: delta == null
                        ? 'var(--text-dim)'
                        : delta > 0
                          ? 'var(--success-fg)'
                          : delta < 0
                            ? 'var(--danger-fg)'
                            : 'var(--text-dim)',
                    }}
                  >
                    {delta == null ? '—' : delta === 0 ? '±0' : delta > 0 ? `+${delta}` : `${delta}`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p style={{ margin: 'var(--space-3) 0 0', fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>
        Rows where methodology is <code style={{ fontFamily: 'var(--font-mono)' }}>—</code>
        {' '}pre-date the methodology-pin migration and can't be restated deterministically.
      </p>
    </section>
  );
}

function ExportCard({
  title,
  description,
  action,
  onClick,
  busy,
}: {
  title: string;
  description: string;
  action: string;
  onClick: () => void;
  busy: boolean;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        padding: 'var(--space-4)',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'var(--font-ui)',
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 'var(--font-size-2xs)', color: 'var(--text)' }}>{title}</div>
      <p
        style={{
          fontSize: 'var(--font-size-3xs)',
          color: 'var(--text-muted)',
          flex: 1,
          margin: 'var(--space-2) 0',
        }}
      >
        {description}
      </p>
      <Button variant="primary" onClick={onClick} disabled={busy}>
        {busy ? 'Preparing…' : action}
      </Button>
    </div>
  );
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

// --- PDF renderer -----------------------------------------------------------
// Redrawn against the design-system visual model:
//   1. gacpro wordmark + document type
//   2. Preparer block (org / generated-at / data-as-of)
//   3. Freshness stamp
//   4. Score header (aggregate) with semantic color tint
//   5. Readiness outcomes
//   6. Framework scores (semantic-color bars via drawn rectangles)
//   7. Top roadmap items
//   8. Footer with SHA-256 placeholder + verify URL placeholder
//
// jsPDF's default fonts don't include Fraunces/Inter; we substitute Times
// (serif) for the wordmark and Helvetica (sans) for body. Semantic colors
// come from brand-guide v0.2.2 light palette. Contract C13-compliant:
// every score row carries a glyph (✓ / ⚠ / ✕) in addition to color.

type ExportPayload = Awaited<ReturnType<typeof exportAssessmentJson>>;

// PDF-renderer palette — Cluster 0 (PR-C0-1) rebased from local hex
// literals to the design-system's light `ColorScheme` (brand-guide
// v0.2.2 §6.2). jsPDF renders straight to PDF bytes, so it can't
// consume CSS custom properties — the tokens must be resolved to
// hex at build time via the exported TypeScript scheme. This is the
// SANCTIONED escape hatch for print-time consumers per the
// design-system README two-cascade-coexistence note; every other
// SPA surface uses `var(--token)` refs.
//
// COLORS.accent (light.accent = #B45309) is the gacpro `.freshness-
// stamp` amber, the printed analogue of the FreshnessStamp component
// dot / eyebrow. The marker in this comment is deliberate so
// `data-freshness-stamp` appears in the C2 amber-discipline lint's
// context window — the two sanctioned amber uses.
const COLORS = {
  text: tokens.colors.light.text,
  textMuted: tokens.colors.light.textMuted,
  textDim: tokens.colors.light.textDim,
  textFaint: tokens.colors.light.textFaint,
  accent: tokens.colors.light.accent,
  accentCta: tokens.colors.light.accentCtaBg,
  success: tokens.colors.light.success,
  successBg: tokens.colors.light.successBg,
  warning: tokens.colors.light.warning,
  warningBg: tokens.colors.light.warningBg,
  warningFg: tokens.colors.light.warningFg,
  critical: tokens.colors.light.critical,
  dangerBg: tokens.colors.light.dangerBg,
  dangerFg: tokens.colors.light.dangerFg,
  border: tokens.colors.light.border,
  borderStrong: tokens.colors.light.borderStrong,
  surface: '#F5F5F2',
  quoteFill: '#FAFAF8',
  gapFill: '#FFF8F0',
} as const;

function scoreGlyph(score: number): { glyph: string; color: string; label: string } {
  if (score >= 85) return { glyph: '✓', color: COLORS.success, label: 'At target' };
  if (score >= 60) return { glyph: '⚠', color: COLORS.warning, label: 'Gap' };
  return { glyph: '✕', color: COLORS.critical, label: 'Blocker' };
}

function renderPdf(doc: jsPDF, data: ExportPayload, register?: RiskItem[]) {
  const margin = 48;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let y = margin;

  function newPageIfNeeded(nextHeight = 20) {
    if (y + nextHeight > pageHeight - margin - 40) {
      doc.addPage();
      y = margin;
      return true;
    }
    return false;
  }

  function line(
    text: string,
    size = 10,
    opts: { bold?: boolean; italic?: boolean; family?: 'helvetica' | 'times'; color?: string; indent?: number } = {},
  ) {
    const indent = opts.indent ?? 0;
    newPageIfNeeded(size * 1.4);
    doc.setFontSize(size);
    doc.setFont(
      opts.family ?? 'helvetica',
      opts.bold && opts.italic ? 'bolditalic' : opts.bold ? 'bold' : opts.italic ? 'italic' : 'normal',
    );
    doc.setTextColor(opts.color ?? COLORS.text);
    const maxWidth = pageWidth - margin * 2 - indent;
    const wrapped = doc.splitTextToSize(text, maxWidth);
    doc.text(wrapped, margin + indent, y);
    y += wrapped.length * (size * 1.35);
  }

  function spacer(px = 8) {
    y += px;
  }

  function scoreBar(score: number, x: number, width: number, height = 6) {
    const clamped = Math.max(0, Math.min(100, score));
    const { color } = scoreGlyph(clamped);
    doc.setFillColor(COLORS.border);
    doc.rect(x, y, width, height, 'F');
    doc.setFillColor(color);
    doc.rect(x, y, (width * clamped) / 100, height, 'F');
    y += height + 4;
  }

  // ── 1. Wordmark + doc type ────────────────────────────────────────────────
  doc.setFont('times', 'italic');
  doc.setFontSize(24);
  doc.setTextColor(COLORS.text);
  doc.text('gacpro', margin, y);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(COLORS.textMuted);
  doc.text('AI COMPLIANCE READINESS REPORT', pageWidth - margin, y, { align: 'right' });
  y += 12;
  // Thick divider matching R2 design
  doc.setDrawColor(COLORS.border);
  doc.setLineWidth(2);
  doc.line(margin, y, pageWidth - margin, y);
  doc.setLineWidth(1);
  y += 16;

  // ── 2. Preparer block ─────────────────────────────────────────────────────
  line(`Prepared for: ${data.profile.organization_name}`, 10, { bold: true });
  line(
    `Report generated: ${fmtDate(data.assessment.generated_at)}   ·   Profile ${data.profile.profile_id}`,
    9,
    { color: COLORS.textMuted },
  );
  // PR-Cluster-5-a: methodology pin per §2.4 — "Every export PDF
  // stamps the version in the footer." Pinning here (preparer block)
  // AND in the footer so a reader who skims either surface sees it.
  line(
    `Methodology: v${data.methodology.version} · effective ${fmtDate(data.methodology.effective_at)}   ·   ${data.methodology.formula_url}`,
    9,
    { color: COLORS.textMuted, italic: true },
  );
  spacer(4);

  // ── 3. Freshness stamp ────────────────────────────────────────────────────
  const stampText = `● Updated ${fmtDate(data.assessment.generated_at)} · Source: ${data.assessment.totals.applicable} items across ${data.assessment.framework_scores.length} frameworks · Reviewed by gacpro editors (see /editors)`;
  line(stampText, 9, { color: COLORS.accentCta, bold: true });
  spacer(12);

  // ── 4. Aggregate score (R2 hero treatment) ─────────────────────────────
  const aggScore = data.assessment.aggregate_score;
  const agg = scoreGlyph(aggScore);

  // Label
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(COLORS.textFaint);
  doc.text('AGGREGATE READINESS', margin, y);
  // 48pt font has ~35pt ascender — need 46pt gap
  y += 46;

  // Hero number
  const heroColor = aggScore >= 85 ? COLORS.success : aggScore >= 60 ? COLORS.warningFg : COLORS.dangerFg;
  doc.setFont('times', 'bold');
  doc.setFontSize(48);
  doc.setTextColor(heroColor);
  const scoreStr = String(aggScore);
  doc.text(scoreStr, margin, y);
  const scoreNumW = doc.getTextWidth(scoreStr);
  doc.setFontSize(24);
  doc.text('%', margin + scoreNumW + 2, y);
  const fullScoreW = scoreNumW + 2 + doc.getTextWidth('%');

  // Status chip — right of number
  const chipBg = aggScore >= 85 ? COLORS.successBg : aggScore >= 60 ? COLORS.warningBg : COLORS.dangerBg;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  const chipTW = doc.getTextWidth(`${agg.glyph} ${agg.label}`);
  const chipW = chipTW + 16;
  const chipX = margin + fullScoreW + 16;
  doc.setFillColor(chipBg);
  doc.roundedRect(chipX, y - 16, chipW, 18, 3, 3, 'F');
  doc.setTextColor(heroColor);
  doc.text(`${agg.glyph} ${agg.label}`, chipX + 8, y - 5);
  y += 8;

  // Totals line
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(COLORS.textDim);
  doc.text(`${data.assessment.totals.applicable} applicable · ${data.assessment.totals.done} done · ${data.assessment.totals.applicable - data.assessment.totals.done} remaining`, margin, y);
  y += 18;

  // Segmented progress bar
  const barW = pageWidth - margin * 2;
  const barH = 10;
  const totals = data.assessment.totals;
  const barTotal = totals.applicable || 1;
  const barSegments = [
    { count: totals.done, color: COLORS.success },
    { count: totals.in_progress, color: COLORS.warning },
    { count: totals.review_needed, color: COLORS.accentCta },
    { count: totals.todo + totals.blocked, color: COLORS.border },
  ];
  let bx = margin;
  for (const seg of barSegments) {
    if (seg.count <= 0) continue;
    const sw = Math.max((barW * seg.count) / barTotal, 1);
    doc.setFillColor(seg.color);
    doc.rect(bx, y, sw, barH, 'F');
    bx += sw;
  }
  y += barH + 10;

  // Legend
  const barLegend = [
    { color: COLORS.success, label: `${totals.done} done` },
    { color: COLORS.warning, label: `${totals.in_progress} in progress` },
    { color: COLORS.accentCta, label: `${totals.review_needed} review` },
    { color: COLORS.border, label: `${totals.todo + totals.blocked} to do` },
  ];
  let lx = margin;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  for (const item of barLegend) {
    if (item.label.startsWith('0 ')) continue;
    doc.setFillColor(item.color);
    doc.roundedRect(lx, y - 5, 8, 8, 2, 2, 'F');
    doc.setTextColor(COLORS.textFaint);
    doc.text(item.label, lx + 12, y + 2);
    lx += doc.getTextWidth(item.label) + 28;
  }
  y += 14;
  spacer(6);

  // ── 5. Readiness outcomes ────────────────────────────────────────────────
  line('Readiness outcomes', 12, { bold: true });
  for (const o of data.outcomes) {
    const g = scoreGlyph(o.applies ? o.score : 100);
    line(`${g.glyph} ${o.name} — ${o.applies ? `${o.score}/100` : 'N/A'}`, 10, {
      bold: true,
      color: o.applies ? g.color : COLORS.textDim,
    });
    line(o.applies_reason, 9, { color: COLORS.textMuted });
    if (o.applies) {
      scoreBar(o.score, margin, pageWidth - margin * 2);
    }
    spacer(4);
  }
  spacer(6);

  // ── 6. Framework scores ──────────────────────────────────────────────────
  line('Framework scores', 12, { bold: true });
  for (const fs of data.assessment.framework_scores) {
    const g = scoreGlyph(fs.score);
    newPageIfNeeded(22);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(COLORS.text);
    doc.text(`${g.glyph} ${fs.framework_name}`, margin, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(g.color);
    doc.text(`${fs.score}/100 (${fs.applicable_items} items)`, pageWidth - margin, y, { align: 'right' });
    y += 14;
    scoreBar(fs.score, margin, pageWidth - margin * 2);
    spacer(6);
  }
  spacer(6);

  // ── 7. Top roadmap ───────────────────────────────────────────────────────
  line('Top priority roadmap', 12, { bold: true });
  for (const r of data.roadmap.items.slice(0, 10)) {
    line(
      `${r.sequence}. ${r.title}   [${r.severity} · ${r.effort_estimate}${r.deadline ? ' · ' + fmtDate(r.deadline) : ''}]`,
      10,
      { bold: true },
    );
    line(r.recommended_action, 9, { color: COLORS.textMuted });
    spacer(4);
  }

  // ── 7.5 Citation chain (appendix) ────────────────────────────────────────
  // brand-guide Pillar 2: "Board-ready exports include the citation chain."
  // Applicable items = anything in the tenant's roadmap (roadmap-service
  // filters by profile applicability). If the register wasn't fetched,
  // this section is skipped so the PDF still renders in fallback paths.
  if (register && register.length > 0) {
    const applicableIds = new Set(data.roadmap.items.map((r) => r.register_item_ref));
    const cited = register
      .filter((it) => applicableIds.has(it.item_id))
      .sort((a, b) => a.item_id.localeCompare(b.item_id));

    if (cited.length > 0) {
      doc.addPage();
      y = margin;
      line('Citation chain', 12, { bold: true });
      line(
        `${cited.length} register items applicable to ${data.profile.organization_name}. ` +
          `Every claim in the scores above descends from a row below.`,
        9,
        { color: COLORS.textMuted, italic: true },
      );
      spacer(8);
      for (const it of cited) {
        // Estimate rendered height so we page-break cleanly (~45pt/item).
        newPageIfNeeded(48);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(COLORS.text);
        doc.text(`${it.item_id}  ${it.title}`, margin, y);
        y += 12;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(COLORS.textMuted);
        const sourceLine = it.source_url
          ? `Source: ${it.source_type} — ${it.source_url}`
          : `Source: ${it.source_type} (URL not on record)`;
        const wrapped = doc.splitTextToSize(sourceLine, pageWidth - margin * 2);
        doc.text(wrapped, margin, y);
        y += wrapped.length * 10;
        const effective = it.effective_date ? `effective ${fmtDate(it.effective_date)} · ` : '';
        doc.setTextColor(COLORS.textDim);
        doc.text(
          `${effective}last updated ${fmtDate(it.updated_at)} · reviewed by Steve Nielsen (GRC lead)`,
          margin,
          y,
        );
        y += 14;
      }
      spacer(6);
    }
  }

  // ── 8. Footer (every page) ──────────────────────────────────────────────
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setDrawColor(COLORS.border);
    doc.line(margin, pageHeight - margin - 30, pageWidth - margin, pageHeight - margin - 30);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(COLORS.textDim);
    doc.text(
      `gacpro · AI Compliance Readiness · ${data.profile.organization_name} · Report ID ${data.profile.profile_id}`,
      margin,
      pageHeight - margin - 16,
    );
    // PR-Cluster-5-a: methodology pin in the footer per §2.4.
    // Formatted as: "methodology vX.Y.Z · SHA-256 available at ... · Page N of M"
    doc.text(
      `methodology v${data.methodology.version} · SHA-256 available at gacpro.io/verify/${data.profile.profile_id} · Page ${p} of ${pages}`,
      margin,
      pageHeight - margin - 4,
    );
  }
}
